import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import authService from '../services/AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const openOAuth2 = async (provider) => {
  if (Capacitor.isNativePlatform()) {
    const url = `${API_BASE_URL}/oauth2/authorization/${provider}?platform=native`;
    await Browser.open({ url, windowName: '_self' });
  } else {
    window.location.href = `${API_BASE_URL}/oauth2/authorization/${provider}?platform=web`;
  }
};

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" style={{ flexShrink: 0 }}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" style={{ flexShrink: 0 }}>
      <path fill="currentColor" d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.39-1.94 2.77-3.43 2.77-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.696.91-1.385 0-2.423-1.29-3.39-2.66-1.937-2.77-3.41-7.83-1.42-11.25.97-1.68 2.71-2.74 4.6-2.77 1.41-.03 2.97.93 3.65.93.66 0 2.43-1.15 4.11-.98.7.03 2.67.28 4.04 2.13-.1.07-2.41 1.42-2.38 4.22.03 3.35 2.96 4.43 3 4.45z" />
    </svg>
  );
}

export default function LoginModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.login(email, password);
      setEmail('');
      setPassword('');
      onSuccess?.();
    } catch {
      setError(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setError('');
    onClose();
  };

  const handleRegisterClick = () => {
    handleClose();
    navigate('/register');
  };

  const isDebugMode = process.env.REACT_APP_DEBUG_MODE === 'true';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="h6" component="span" fontWeight={700} color="primary">
          {t('auth.login')}
        </Typography>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {isDebugMode && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
            <strong>DEBUG MODE</strong> — cualquier credencial es válida.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
        >
          <TextField
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoComplete="email"
            autoFocus
          />
          <TextField
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="current-password"
          />
          <Link
            component="button"
            type="button"
            onClick={() => { handleClose(); navigate('/forgot-password'); }}
            underline="hover"
            variant="body2"
            sx={{ alignSelf: 'flex-end', mt: -1 }}
          >
            {t('auth.forgotPassword')}
          </Link>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || !email || !password}
            fullWidth
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.loginButton')}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('auth.orContinueWith')}
          </Typography>
        </Divider>

        <Button
          variant="contained"
          fullWidth
          size="small"
          startIcon={<AppleIcon />}
          onClick={() => openOAuth2('apple')}
          sx={{ textTransform: 'none', mb: 1, bgcolor: '#000', color: '#fff', '&:hover': { bgcolor: '#222' } }}
        >
          {t('auth.continueWithApple')}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          size="small"
          startIcon={<GoogleIcon />}
          onClick={() => openOAuth2('google')}
          sx={{ textTransform: 'none' }}
        >
          {t('auth.continueWithGoogle')}
        </Button>

        <Typography variant="body2" textAlign="center" mt={2}>
          {t('auth.noAccount')}{' '}
          <Link component="button" type="button" onClick={handleRegisterClick} underline="hover">
            {t('auth.registerHere')}
          </Link>
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
